import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ca } from '@cian/analytics';

import { useAppDispatch, useAppSelector } from './redux';
import { IBreadItem } from '../types/bread_crumb';
import { IClientAddForm } from '../types/client_add_form';
import { IPostItem } from '../types/posts';
import { ISeoItem } from '../types/seo_data';
import { TReduxActions } from '../types/redux';
import { setBreadCrumb } from '../reducers/modules/bread_crumbs/bread_crumbs';
import { fetchUser } from '../reducers/modules/current_user';
import { getSeoSuccess } from '../reducers/modules/seo/seo';
import { getRegions } from '../reducers/modules/regions/regions';
import { getTags } from '../reducers/modules/tags/tags';
import { getPostCard, notEditCard, editCard } from '../reducers/modules/post_card/post_card';
import { combineDataLayerObject } from '../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../app/helpers/breadcrumbs_helper';
import { prepareQSParam } from '../utils';

interface IUsePostFormInitParams {
  crumbs: IBreadItem[];
  seo: ISeoItem;
  postCardEndpoint: string;
  formState: IClientAddForm;
  cleanupStorage: () => TReduxActions;
  redirectPathBuilder: (slug: string, id: number) => string;
  fetchUserAfterSubmit?: boolean;
}

export const usePostFormInit = ({
  crumbs,
  seo,
  postCardEndpoint,
  formState,
  cleanupStorage,
  redirectPathBuilder,
  fetchUserAfterSubmit = false,
}: IUsePostFormInitParams) => {
  const dispatch = useAppDispatch();
  const browserHistory = useHistory();
  const { search } = useLocation();

  const config = useAppSelector(state => state.config);
  const analytics = useAppSelector(state => state.analytics);
  const deviceType = useAppSelector(state => state.deviceType);
  const user = useAppSelector(state => state.user);
  const regions = useAppSelector(state => state.regions);
  const tags = useAppSelector(state => state.tags);
  const postCard = useAppSelector(state => state.postCard);

  const [editingItem, setEditingItem] = useState<IPostItem | undefined>(undefined);

  const qsPostSlugAndId = prepareQSParam(new URLSearchParams(search).get('slug-id'));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    dispatch(setBreadCrumb(crumbs));
    dispatch(getRegions(config.regionsApiSchemeNode, config.regionsApiHostNode));
    dispatch(getTags());
    dispatch(getSeoSuccess(seo));

    if (qsPostSlugAndId) {
      dispatch(editCard());
      dispatch(getPostCard(postCardEndpoint, [
        { name: 'id', value: qsPostSlugAndId },
        { name: 'user_id', value: user.userId },
      ]));
    } else if (formState.isEdit) {
      setEditingItem(postCard.item);
    }

    dispatch(cleanupStorage());

    return () => {
      if (formState.isEdit) {
        dispatch(notEditCard());
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (postCard.item.id) {
      setEditingItem(postCard.item);
    }
  }, [postCard.item]);

  useEffect(() => {
    if (formState.item?.id) {
      if (fetchUserAfterSubmit) {
        dispatch(fetchUser());
      }
      browserHistory.push(redirectPathBuilder(formState.item.attributes.slug, formState.item.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.item]);

  return {
    user,
    regions,
    tags,
    editingItem,
    deviceType,
  };
};
